import { FC } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FormComponent, FormRow, FormText } from '@chargeafter/forms';
import {
  LinkDeliveryMethod,
  LinkFlow,
  LinkInteraction,
  useGetLinkInteraction,
} from '../../services/sendLinkManager';
import { Typography, VerticalSpace } from '@chargeafter/ui';
import { useGetSelectedSettleOption } from '../../services/configManager';
import { ButtonName, ESendLinkModal } from '../../constants';
import { MainButton } from '../MainButton';
import { ContentContainer } from './ContentContainer';
import ToggleGroup from '../../components/ToggleGroup';

type ConsumerLinkDetailsProps = {
  submitting: boolean;
  invalid: boolean;
  isLoading: boolean;
};

export const ConsumerLinkDetails: FC<ConsumerLinkDetailsProps> = ({
  submitting,
  invalid,
  isLoading,
}) => {
  const linkInteraction = useGetLinkInteraction();
  const selectedSettleOption = useGetSelectedSettleOption();

  const settlmentOptions = [
    {
      label: ESendLinkModal.AutomaticLabel,
      value: 'Automatic',
    },
    { label: ESendLinkModal.ManualLabel, value: 'Manual' },
  ];
  const isCheckout = linkInteraction.action !== LinkFlow.Apply;
  const sms = linkInteraction.deliveryMethod === LinkDeliveryMethod.Sms;
  const qrCode = linkInteraction.deliveryMethod === LinkDeliveryMethod.QrCode;

  return (
    <ContentContainer>
      <FormRow>
        <FormText name="firstName" label="First Name" disabled={submitting} />
        <FormText name="lastName" label="Last Name" disabled={submitting} />
        <FormText
          name={sms ? 'mobilePhoneNumber' : 'email'}
          label={sms ? 'Phone Number' : 'Email Address'}
          disabled={submitting}
        />
      </FormRow>

      {selectedSettleOption === 'AutomaticAndManual' && isCheckout && (
        <FormRow>
          <FormComponent
            name="autoSettle"
            component={({ input, meta }) => {
              return (
                <Box mt={2}>
                  <Typography>
                    {ESendLinkModal.SettlePreferenceDescription}
                  </Typography>
                  <ToggleGroup
                    data-testId="transaction-settle-switch"
                    exclusive
                    {...input}
                    {...meta}
                    value={input.value || null}
                    onChange={(_, option) => {
                      input.onChange(option);
                    }}
                    options={settlmentOptions}
                  />
                </Box>
              );
            }}
          />
        </FormRow>
      )}
      <VerticalSpace units={2} />
      <MainButton
        variant="contained"
        fullWidth
        type="submit"
        disabled={submitting || invalid}
        buttonName={
          qrCode ? ButtonName.LinkModalNext : ButtonName.LinkModalSendLink
        }
      >
        {!isLoading ? (
          getModalButtonText(linkInteraction)
        ) : (
          <CircularProgress color="inherit" size={35} thickness={5} />
        )}
      </MainButton>
    </ContentContainer>
  );
};

const getModalButtonText = (interaction: LinkInteraction) => {
  if (interaction.deliveryMethod === LinkDeliveryMethod.QrCode)
    return ESendLinkModal.Next;
  return ESendLinkModal.SendLinkButton;
};
